import React, { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import managementService from '../../services/managementService';
import { config } from '../../config/environment';

interface Tenant {
  tenant_id: string;
  tenant_name: string;
  description?: string;
  created: string;
}

interface TenantSelectorProps {
  variant?: 'navigation' | 'inline';
  className?: string;
}

const TenantSelector: React.FC<TenantSelectorProps> = ({ 
  variant = 'navigation',
  className = ''
}) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { selectedTenant, setSelectedTenant, tenantName } = useTenant();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show tenant selector for admins (case-insensitive check)
  const userRole = user?.managementUser?.role;
  const shouldShowSelector = userRole?.toLowerCase() === 'admin';
  console.log('TenantSelector - user role:', userRole, 'shouldShowSelector:', shouldShowSelector);

  // Fetch tenants for admin users
  useEffect(() => {
    const fetchTenants = async () => {
      if (!shouldShowSelector || config.useDummyData) return;
      
      setIsLoading(true);
      setError(null);
      try {
        console.log('TenantSelector - Fetching tenants...');
        const response = await managementService.getTenants();
        console.log('TenantSelector - Tenants response:', response);
        if (response.success && response.tenants) {
          console.log('TenantSelector - Setting tenants:', response.tenants);
          setTenants(response.tenants);
        } else if (response.error) {
          console.error('TenantSelector - Error in response:', response.error);
          setError(response.error);
        } else {
          console.warn('TenantSelector - No tenants in response:', response);
          setTenants([]);
        }
      } catch (error: any) {
        console.error('TenantSelector - Error fetching tenants:', error);
        setError(error.message || 'Failed to load tenants');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenants();
  }, [shouldShowSelector]);

  const handleTenantSelect = async (tenantId: string) => {
    await setSelectedTenant(tenantId);
    setIsOpen(false);
  };

  // Show loading state while auth is initializing
  if (isAuthLoading) {
    return (
      <div className={`text-sm font-medium text-gray-700 ${className}`}>
        Loading...
      </div>
    );
  }

  // For non-admin users, just show the tenant name
  if (!shouldShowSelector) {
    return (
      <div className={`text-sm font-medium text-gray-700 ${className}`}>
        {tenantName}
      </div>
    );
  }

  // For admin users, show dropdown selector
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <span>{tenantName}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="py-1">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                Loading tenants...
              </div>
            ) : error ? (
              <div className="px-3 py-2">
                <div className="text-sm font-medium text-red-600 mb-1">
                  Cannot load tenants
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  Database connection failed. Please ensure your local environment is configured.
                </div>
                <div className="text-xs text-gray-500 italic">
                  Set DB_URL in your environment or use ./start.zsh with proper credentials
                </div>
              </div>
            ) : tenants.length === 0 ? (
              <div className="px-3 py-2">
                <div className="text-sm font-medium text-gray-700 mb-1">
                  No tenants found
                </div>
                <div className="text-xs text-gray-500">
                  Create a tenant first or check your database
                </div>
              </div>
            ) : (
              tenants.map((tenant) => (
                <button
                  key={tenant.tenant_id}
                  onClick={() => handleTenantSelect(tenant.tenant_id)}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                    selectedTenant === tenant.tenant_id
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-700'
                  }`}
                >
                  <div className="font-medium">{tenant.tenant_name}</div>
                  {tenant.description && (
                    <div className="text-xs text-gray-500">{tenant.description}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TenantSelector;
